import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Trash2, Building2, X, ToggleLeft, ToggleRight, Copy, Check, ChevronDown, ChevronUp, Pencil, Users, FileText, Upload, Image as ImageIcon, Home, LogOut, BarChart3, Search, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Companies = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyEmployees, setNewCompanyEmployees] = useState("");
  const [newSectorInputs, setNewSectorInputs] = useState({});
  const [newFunctionInputs, setNewFunctionInputs] = useState({});
  const [error, setError] = useState("");
  const [togglingAccess, setTogglingAccess] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [expandedCompanies, setExpandedCompanies] = useState({});
  
  // Edit employees state
  const [editingEmployees, setEditingEmployees] = useState(null);
  const [editEmployeesValue, setEditEmployeesValue] = useState("");
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Edit details modal state (CNPJ + Logo)
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsCompany, setDetailsCompany] = useState(null);
  const [detailsCnpj, setDetailsCnpj] = useState("");
  const [detailsLogoBase64, setDetailsLogoBase64] = useState("");
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [detailsError, setDetailsError] = useState("");

  // Filtros da lista de empresas
  const [selectedCompanyId, setSelectedCompanyId] = useState("");

  // Listas separadas por status
  const activeCompanies = companies.filter((c) => c.access_enabled !== false);
  const inactiveCompanies = companies.filter((c) => c.access_enabled === false);
  // Empresa selecionada (a que será exibida em detalhe)
  const selectedCompany = selectedCompanyId
    ? companies.find((c) => c.id === selectedCompanyId)
    : null;

  const openDetailsModal = (company) => {
    setDetailsCompany(company);
    setDetailsCnpj(company.cnpj || "");
    setDetailsLogoBase64(company.logo_base64 || "");
    setDetailsError("");
    setShowDetailsModal(true);
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setDetailsCompany(null);
    setDetailsCnpj("");
    setDetailsLogoBase64("");
    setDetailsError("");
  };

  const formatCnpj = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 14);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
    if (digits.length <= 12) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setDetailsError("O arquivo selecionado não é uma imagem.");
      return;
    }
    if (file.size > 500 * 1024) {
      setDetailsError("Logotipo deve ter no máximo 500KB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setDetailsLogoBase64(reader.result);
      setDetailsError("");
    };
    reader.onerror = () => setDetailsError("Falha ao ler o arquivo.");
    reader.readAsDataURL(file);
  };

  const handleSaveDetails = async () => {
    if (!detailsCompany) return;
    setDetailsSaving(true);
    setDetailsError("");
    try {
      const token = localStorage.getItem("admin_token");
      await axios.patch(
        `${API}/companies/${detailsCompany.id}/details`,
        { cnpj: detailsCnpj.trim(), logo_base64: detailsLogoBase64 || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCompanies();
      closeDetailsModal();
    } catch (err) {
      setDetailsError(err.response?.data?.detail || "Erro ao salvar dados.");
    } finally {
      setDetailsSaving(false);
    }
  };

  const toggleCompanyExpanded = (companyId) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  };

  const handleCopyCode = async (code, companyId) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(companyId);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) {
      setError("Digite o nome da empresa");
      return;
    }
    setError("");

    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/companies`, { 
        empresa: newCompanyName,
        numero_funcionarios: parseInt(newCompanyEmployees) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewCompanyName("");
      setNewCompanyEmployees("");
      fetchCompanies();
    } catch (error) {
      setError("Erro ao cadastrar empresa");
    }
  };

  const openDeleteModal = (company) => {
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setCompanyToDelete(null);
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/companies/${companyToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCompanies();
      closeDeleteModal();
    } catch (error) {
      console.error("Erro ao excluir:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleAccess = async (companyId) => {
    setTogglingAccess(companyId);
    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/companies/${companyId}/toggle-access`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCompanies();
    } catch (error) {
      console.error("Erro ao alternar acesso:", error);
    } finally {
      setTogglingAccess(null);
    }
  };

  const handleAddSector = async (companyId) => {
    const sectorName = newSectorInputs[companyId];
    if (!sectorName?.trim()) return;

    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/companies/${companyId}/sectors`, 
        { sector_name: sectorName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewSectorInputs({ ...newSectorInputs, [companyId]: "" });
      fetchCompanies();
    } catch (error) {
      console.error("Erro ao adicionar setor:", error);
    }
  };

  const handleRemoveSector = async (companyId, sectorName) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/companies/${companyId}/sectors`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { sector_name: sectorName }
      });
      fetchCompanies();
    } catch (error) {
      console.error("Erro ao remover setor:", error);
    }
  };

  const handleAddFunction = async (companyId, sectorName) => {
    const key = `${companyId}-${sectorName}`;
    const functionName = newFunctionInputs[key];
    if (!functionName?.trim()) return;

    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/companies/${companyId}/functions`,
        { sector_name: sectorName, function_name: functionName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewFunctionInputs({ ...newFunctionInputs, [key]: "" });
      fetchCompanies();
    } catch (error) {
      console.error("Erro ao adicionar função:", error);
    }
  };

  const handleRemoveFunction = async (companyId, sectorName, functionName) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/companies/${companyId}/functions`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { sector_name: sectorName, function_name: functionName }
      });
      fetchCompanies();
    } catch (error) {
      console.error("Erro ao remover função:", error);
    }
  };

  const startEditingEmployees = (company) => {
    setEditingEmployees(company.id);
    setEditEmployeesValue(company.numero_funcionarios?.toString() || "0");
  };

  const cancelEditingEmployees = () => {
    setEditingEmployees(null);
    setEditEmployeesValue("");
  };

  const handleUpdateEmployees = async (companyId) => {
    try {
      const token = localStorage.getItem("admin_token");
      await axios.patch(`${API}/companies/${companyId}/employees`, 
        { numero_funcionarios: parseInt(editEmployeesValue) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingEmployees(null);
      setEditEmployeesValue("");
      fetchCompanies();
    } catch (error) {
      console.error("Erro ao atualizar funcionários:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header — pattern from Dashboard Administrativo */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestão de Empresas e Unidades</h1>
            <p className="text-sm text-gray-500">Cadastros, códigos de acesso e estrutura organizacional</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => navigate("/")} data-testid="home-button" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all text-sm">
              <Home className="w-4 h-4" />
              Inicio
            </button>
            <button onClick={() => navigate("/dashboard")} data-testid="dashboard-button" className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all text-sm">
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("admin_token");
                navigate("/login");
              }}
              data-testid="logout-button"
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all text-sm"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto w-full p-6 space-y-6">
        {/* Cadastrar Nova Empresa — padronizado */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
            Cadastrar Nova Empresa
          </h2>
          <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
            Adicione uma nova empresa ao sistema. Após o cadastro, será gerado automaticamente
            um código de acesso único que os colaboradores utilizarão para responder ao
            questionário psicossocial.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={newCompanyName}
              onChange={(e) => setNewCompanyName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateCompany()}
              placeholder="Ex: ViaSeg Medicina Ocupacional"
              className="flex-1 h-11 px-3 rounded-md border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all outline-none text-sm"
              data-testid="new-company-input"
            />
            <input
              type="number"
              value={newCompanyEmployees}
              onChange={(e) => setNewCompanyEmployees(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCreateCompany()}
              placeholder="N° de funcionários"
              min="0"
              className="w-full sm:w-40 h-11 px-3 rounded-md border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all outline-none text-sm"
              data-testid="new-company-employees-input"
            />
            <button
              onClick={handleCreateCompany}
              className="px-5 py-2.5 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-all font-semibold uppercase text-xs tracking-wide"
              data-testid="create-company-btn"
            >
              Cadastrar
            </button>
          </div>
          {error && (
            <p className="text-red-500 text-xs mt-2">{error}</p>
          )}
        </div>

        {/* Empresas Gerenciadas — padronizado */}
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h2 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-2">
            Empresas Gerenciadas
            <span className="ml-3 text-xs text-gray-500 normal-case font-semibold tracking-normal">
              ({companies.length} cadastrada{companies.length === 1 ? "" : "s"} · {activeCompanies.length} ativa{activeCompanies.length === 1 ? "" : "s"} · {inactiveCompanies.length} inativa{inactiveCompanies.length === 1 ? "" : "s"})
            </span>
          </h2>
          <p className="text-xs text-gray-700 leading-relaxed text-justify mb-3">
            Selecione uma empresa em um dos dois campos abaixo — <strong>Ativas</strong> ou
            <strong> Inativas</strong> — para visualizar e gerenciar seus dados. Os dados só
            serão exibidos após a seleção.
          </p>

          {/* Seletores separados: Ativas | Inativas */}
          {companies.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4" data-testid="company-selector-toolbar">
              {/* ATIVAS */}
              <div className="border border-green-300 rounded-md p-2 bg-green-50/30" data-testid="active-selector-wrapper">
                <label className="flex items-center gap-1.5 text-[9px] uppercase tracking-wide text-green-700 font-semibold mb-1">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  Empresas Ativas
                  <span className="ml-auto text-[10px] font-bold text-green-800 bg-green-100 rounded-full px-1.5 py-0.5">
                    {activeCompanies.length}
                  </span>
                </label>
                <div className="relative">
                  <select
                    data-testid="active-company-selector"
                    value={selectedCompany && selectedCompany.access_enabled !== false ? selectedCompanyId : ""}
                    onChange={(e) => setSelectedCompanyId(e.target.value)}
                    disabled={activeCompanies.length === 0}
                    className="w-full h-9 px-2 pr-8 rounded border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all outline-none bg-white appearance-none text-sm text-gray-800 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {activeCompanies.length === 0
                        ? "Nenhuma empresa ativa"
                        : `Selecione uma empresa ativa (${activeCompanies.length})`}
                    </option>
                    {activeCompanies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.empresa}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {/* INATIVAS */}
              <div className="border border-gray-300 rounded-md p-2 bg-gray-50/50" data-testid="inactive-selector-wrapper">
                <label className="flex items-center gap-1.5 text-[9px] uppercase tracking-wide text-gray-600 font-semibold mb-1">
                  <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                  Empresas Inativas
                  <span className="ml-auto text-[10px] font-bold text-gray-700 bg-gray-200 rounded-full px-1.5 py-0.5">
                    {inactiveCompanies.length}
                  </span>
                </label>
                <div className="relative">
                  <select
                    data-testid="inactive-company-selector"
                    value={selectedCompany && selectedCompany.access_enabled === false ? selectedCompanyId : ""}
                    onChange={(e) => setSelectedCompanyId(e.target.value)}
                    disabled={inactiveCompanies.length === 0}
                    className="w-full h-9 px-2 pr-8 rounded border border-gray-200 focus:border-gray-500 focus:ring-2 focus:ring-gray-100 transition-all outline-none bg-white appearance-none text-sm text-gray-800 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">
                      {inactiveCompanies.length === 0
                        ? "Nenhuma empresa inativa"
                        : `Selecione uma empresa inativa (${inactiveCompanies.length})`}
                    </option>
                    {inactiveCompanies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.empresa}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </div>
          )}

          {companies.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-md p-8 text-center" data-testid="empty-companies-state">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-sm text-gray-500 mb-1">Nenhuma empresa cadastrada</p>
              <p className="text-[10px] text-gray-400">Utilize o formulário acima para criar o primeiro cadastro.</p>
            </div>
          ) : !selectedCompany ? (
            <div className="border border-dashed border-gray-300 rounded-md p-10 text-center" data-testid="no-company-selected-state">
              <Building2 className="w-16 h-16 mx-auto mb-3 text-gray-300" />
              <h4 className="text-lg font-semibold text-gray-500 mb-1">Selecione uma Empresa</h4>
              <p className="text-sm text-gray-400 max-w-md mx-auto">
                Utilize o filtro &quot;Empresa / Unidade&quot; acima para visualizar e gerenciar os dados
                de uma empresa específica.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {[selectedCompany].map((company) => (
                <div key={company.id} className="bg-white rounded-lg p-5 border border-gray-200" data-testid={`company-card-${company.id}`}>
                  {/* Company Header — standardized to match page pattern */}
                  <h3 className="text-base font-bold text-gray-900 uppercase tracking-wide border-b-2 border-orange-500 pb-1 mb-3">
                    {company.empresa}
                    <span className="ml-3 text-xs text-gray-500 normal-case font-semibold tracking-normal">
                      {company.access_enabled !== false ? "· Ativa" : "· Inativa"}
                    </span>
                  </h3>

                  <div className="flex justify-between items-start mb-4">
                    <div>
                      {/* Editable Employee Count */}
                      <div className="flex items-center gap-2 mt-1">
                        {editingEmployees === company.id ? (
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-purple-500" />
                            <input
                              type="number"
                              min="0"
                              value={editEmployeesValue}
                              onChange={(e) => setEditEmployeesValue(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') handleUpdateEmployees(company.id);
                                if (e.key === 'Escape') cancelEditingEmployees();
                              }}
                              className="w-20 h-8 px-2 text-sm border border-purple-300 rounded-lg focus:border-purple-500 focus:ring-1 focus:ring-purple-200 outline-none"
                              autoFocus
                              data-testid={`edit-employees-input-${company.id}`}
                            />
                            <span className="text-sm text-gray-500">funcionários</span>
                            <button
                              onClick={() => handleUpdateEmployees(company.id)}
                              className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                              title="Salvar"
                              data-testid={`save-employees-${company.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEditingEmployees}
                              className="p-1.5 bg-gray-100 text-gray-500 rounded-lg hover:bg-gray-200 transition-colors"
                              title="Cancelar"
                              data-testid={`cancel-employees-${company.id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditingEmployees(company)}
                            className="flex items-center gap-2 text-sm text-gray-500 hover:text-purple-600 transition-colors group"
                            title="Clique para editar"
                            data-testid={`edit-employees-btn-${company.id}`}
                          >
                            <Users className="w-4 h-4 text-purple-500" />
                            <span className="font-medium text-purple-600">{company.numero_funcionarios || 0}</span>
                            <span>funcionários</span>
                            <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-500 uppercase">Código de Acesso:</span>
                        <span className={`px-3 py-1 font-mono font-bold text-sm rounded ${
                          company.access_enabled !== false 
                            ? 'bg-orange-100 text-orange-700' 
                            : 'bg-gray-200 text-gray-400 line-through'
                        }`}>
                          {company.access_code}
                        </span>
                        
                        {/* Copy Button */}
                        <button
                          onClick={() => handleCopyCode(company.access_code, company.id)}
                          className={`p-1.5 rounded-lg transition-all ${
                            copiedCode === company.id
                              ? 'bg-green-100 text-green-600'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700'
                          }`}
                          title="Copiar código"
                          data-testid={`copy-code-${company.id}`}
                        >
                          {copiedCode === company.id ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                        
                        {/* Toggle Access Button */}
                        <button
                          onClick={() => handleToggleAccess(company.id)}
                          disabled={togglingAccess === company.id}
                          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold transition-all ml-2 ${
                            company.access_enabled !== false
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-red-100 text-red-600 hover:bg-red-200'
                          }`}
                          data-testid={`toggle-access-${company.id}`}
                          title={company.access_enabled !== false ? 'Desabilitar acesso' : 'Habilitar acesso'}
                        >
                          {company.access_enabled !== false ? (
                            <>
                              <ToggleRight className="w-4 h-4" />
                              <span>Ativo</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-4 h-4" />
                              <span>Inativo</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openDetailsModal(company)}
                        className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg transition-colors"
                        data-testid={`edit-details-${company.id}`}
                        title="Editar CNPJ e logotipo do cliente"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        <span>Dados &amp; Logo</span>
                      </button>
                      <button
                        onClick={() => openDeleteModal(company)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        data-testid={`delete-company-${company.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Expand/Collapse Button */}
                  <button
                    onClick={() => toggleCompanyExpanded(company.id)}
                    className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-all mb-2"
                    data-testid={`toggle-expand-${company.id}`}
                  >
                    {expandedCompanies[company.id] ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        <span>Ocultar Setores e Funções</span>
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        <span>Mostrar Setores e Funções ({company.sectors?.length || 0} setores)</span>
                      </>
                    )}
                  </button>

                  {/* Collapsible Section */}
                  {expandedCompanies[company.id] && (
                    <>
                      {/* Add Sector */}
                      <div className="flex gap-2 mb-4">
                        <input
                          type="text"
                          value={newSectorInputs[company.id] || ""}
                          onChange={(e) => setNewSectorInputs({ ...newSectorInputs, [company.id]: e.target.value })}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddSector(company.id)}
                          placeholder="Nome do Novo Setor"
                          className="flex-1 h-10 px-4 rounded-lg border border-gray-200 focus:border-gray-400 focus:ring-1 focus:ring-gray-200 transition-all outline-none text-sm"
                          data-testid={`add-sector-input-${company.id}`}
                        />
                        <button
                          onClick={() => handleAddSector(company.id)}
                          className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all font-semibold uppercase text-xs"
                          data-testid={`add-sector-btn-${company.id}`}
                        >
                          Add Setor
                        </button>
                      </div>

                      {/* Sectors List */}
                      {company.sectors && company.sectors.length > 0 && (
                        <div className="space-y-4">
                          {company.sectors.map((sector, sectorIdx) => (
                            <div key={sectorIdx} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                              {/* Sector Header */}
                              <div className="flex justify-between items-center mb-3">
                                <h4 className="font-bold text-gray-700 uppercase text-sm">{sector.name}</h4>
                                <button
                                  onClick={() => handleRemoveSector(company.id, sector.name)}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                  data-testid={`remove-sector-${company.id}-${sector.name}`}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>

                              {/* Functions Tags */}
                              <div className="flex flex-wrap gap-2 mb-3">
                                {sector.functions && sector.functions.map((func, funcIdx) => (
                                  <span
                                    key={funcIdx}
                                    className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded text-sm font-medium"
                                  >
                                    {func}
                                    <button
                                      onClick={() => handleRemoveFunction(company.id, sector.name, func)}
                                      className="text-orange-500 hover:text-orange-700"
                                      data-testid={`remove-function-${company.id}-${sector.name}-${func}`}
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>

                              {/* Add Function */}
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={newFunctionInputs[`${company.id}-${sector.name}`] || ""}
                                  onChange={(e) => setNewFunctionInputs({ 
                                    ...newFunctionInputs, 
                                    [`${company.id}-${sector.name}`]: e.target.value 
                                  })}
                                  onKeyPress={(e) => e.key === 'Enter' && handleAddFunction(company.id, sector.name)}
                                  placeholder="Nova Função (ex: Operador)"
                                  className="flex-1 h-9 px-3 rounded-lg border border-gray-200 focus:border-orange-400 focus:ring-1 focus:ring-orange-100 transition-all outline-none text-sm"
                                  data-testid={`add-function-input-${company.id}-${sector.name}`}
                                />
                                <button
                                  onClick={() => handleAddFunction(company.id, sector.name)}
                                  className="px-3 py-1.5 bg-white border-2 border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition-all font-semibold uppercase text-xs"
                                  data-testid={`add-function-btn-${company.id}-${sector.name}`}
                                >
                                  Add Função
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
            {/* Trash Icon */}
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Trash2 className="w-8 h-8 text-red-500" />
            </div>
            
            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Excluir Empresa?</h2>
            
            {/* Description */}
            <p className="text-gray-600 mb-8">
              Tem certeza que deseja excluir esta empresa?<br />
              <span className="text-red-600 font-medium">Todos os dados vinculados serão perdidos permanentemente.</span>
            </p>
            
            {/* Company name being deleted */}
            {companyToDelete && (
              <div className="mb-6 p-3 bg-gray-100 rounded-lg">
                <p className="text-sm text-gray-500">Empresa:</p>
                <p className="font-bold text-gray-900">{companyToDelete.empresa}</p>
              </div>
            )}
            
            {/* Buttons */}
            <div className="flex gap-4">
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all font-semibold uppercase"
                data-testid="cancel-delete-btn"
              >
                Não
              </button>
              <button
                onClick={handleDeleteCompany}
                disabled={deleting}
                className="flex-1 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-semibold uppercase disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {deleting ? "Excluindo..." : "Sim, Excluir"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Details Modal (CNPJ + Logo) */}
      {showDetailsModal && detailsCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="details-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Dados &amp; Logotipo do Cliente</h2>
                  <p className="text-xs text-gray-500">{detailsCompany.empresa}</p>
                </div>
              </div>
              <button onClick={closeDetailsModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="close-details-modal">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-gray-500 mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 leading-relaxed">
              Estes dados aparecem <strong>apenas na capa do relatório multisetorial impresso</strong> (não aparecem
              quando um setor específico é filtrado). Ambos os campos são opcionais.
            </p>

            {/* CNPJ */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                CNPJ (opcional)
              </label>
              <input
                type="text"
                value={detailsCnpj}
                onChange={(e) => setDetailsCnpj(formatCnpj(e.target.value))}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
                data-testid="details-cnpj-input"
              />
            </div>

            {/* Logo */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                Logotipo do Cliente (opcional, máx 500KB)
              </label>

              <div className="flex items-start gap-3">
                {/* Preview */}
                <div className="w-24 h-24 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {detailsLogoBase64 ? (
                    <img
                      src={detailsLogoBase64}
                      alt="Preview"
                      className="max-w-full max-h-full object-contain"
                      data-testid="details-logo-preview"
                    />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  )}
                </div>

                <div className="flex-1 space-y-2">
                  <label
                    htmlFor="logo-upload-input"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg cursor-pointer transition-colors"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    {detailsLogoBase64 ? "Trocar imagem" : "Selecionar imagem"}
                  </label>
                  <input
                    id="logo-upload-input"
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    onChange={handleLogoFileChange}
                    className="hidden"
                    data-testid="details-logo-file-input"
                  />

                  {detailsLogoBase64 && (
                    <button
                      type="button"
                      onClick={() => setDetailsLogoBase64("")}
                      className="block text-xs text-red-500 hover:underline"
                      data-testid="details-logo-remove"
                    >
                      Remover logotipo
                    </button>
                  )}

                  <p className="text-[10px] text-gray-500 leading-snug">
                    Formatos: PNG, JPG, WebP, SVG. Fundo transparente recomendado.
                  </p>
                </div>
              </div>
            </div>

            {detailsError && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600" data-testid="details-error">
                {detailsError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={closeDetailsModal}
                disabled={detailsSaving}
                className="flex-1 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all font-semibold uppercase text-sm disabled:opacity-50"
                data-testid="cancel-details-btn"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveDetails}
                disabled={detailsSaving}
                className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all font-semibold uppercase text-sm disabled:opacity-50"
                data-testid="save-details-btn"
              >
                {detailsSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;
